import type { RunSummary } from "./types.js";

export interface AttentionPriority {
  actNow: boolean;
  label: string;
  reason: string;
  rank: number;
}

type AttentionRun = Pick<RunSummary, "appointmentAt" | "appointmentConfirmedAt" | "attentionSource" | "bucket" | "currentTask" | "stage">;

export function attentionPriority(run: AttentionRun): AttentionPriority | null {
  if (run.attentionSource === "appointment_reschedule_request") {
    return { actNow: true, label: "Seller needs a new time", reason: "The old reminder is canceled. Choose a new appointment time.", rank: 6 };
  }
  if (run.stage === "handoff_ready") {
    return { actNow: true, label: "Ready to assign", reason: "The seller is qualified and the appointment is booked.", rank: 5 };
  }
  if (run.bucket !== "needs_you") return null;
  if (run.attentionSource === "seller_call_request") {
    return { actNow: true, label: "Seller asked for a call", reason: "Review the requested time and let Cal take the approved call.", rank: 5 };
  }
  if (run.attentionSource === "appointment_conflict") {
    return { actNow: true, label: "Appointment needs a new time", reason: "The offered time was taken before the seller could book it.", rank: 5 };
  }
  if (run.appointmentAt || run.stage === "appointment_set") {
    return {
      actNow: Boolean(run.appointmentConfirmedAt),
      label: run.appointmentConfirmedAt ? "Confirmed appointment needs prep" : "Booked appointment needs prep",
      reason: "A seller appointment is booked. Finish the human prep before it starts.",
      rank: run.appointmentConfirmedAt ? 4 : 3
    };
  }
  if (run.attentionSource === "closed_inbound_guard") {
    return { actNow: false, label: "Seller replied after close", reason: "Read the new reply and decide whether this lead should reopen.", rank: 3 };
  }
  if (run.attentionSource === "lead_disposition_reopened") {
    return { actNow: false, label: "Review before outreach", reason: "This lead was reopened with Hope and Cal still paused.", rank: 2 };
  }
  if (run.attentionSource === "consent_required" || run.stage === "researching") {
    return { actNow: false, label: "Contact permission needed", reason: "Record how the seller asked to be contacted before an agent reaches out.", rank: 1 };
  }
  return { actNow: false, label: "Review this lead", reason: run.currentTask, rank: 2 };
}

export function compareAttention(a: AttentionRun & Pick<RunSummary, "attentionSince">, b: AttentionRun & Pick<RunSummary, "attentionSince">): number {
  const aPriority = attentionPriority(a);
  const bPriority = attentionPriority(b);
  const rankDifference = (bPriority?.rank ?? 0) - (aPriority?.rank ?? 0);
  if (rankDifference) return rankDifference;
  if (a.appointmentAt && b.appointmentAt) {
    const appointmentDifference = new Date(a.appointmentAt).getTime() - new Date(b.appointmentAt).getTime();
    if (appointmentDifference) return appointmentDifference;
  }
  return new Date(a.attentionSince ?? 0).getTime() - new Date(b.attentionSince ?? 0).getTime();
}
